import { useSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { MutationInputError } from 'providers/tanstack-query/types'
import { useMutation } from 'providers/tanstack-query/useMutation'
import { UPDATE_DOMAIN_PRODUCT_ENABLED } from './api/mutation'
import { Data, Variables } from './api/types'
import { SwitchVariables, UseSwitchDomainProduct, UseSwitchDomainProductProps } from './types'

export const useSwitchDomainProduct = ({
  name,
  product,
  value,
}: UseSwitchDomainProductProps): UseSwitchDomainProduct => {
  const { t } = useTranslation('features/domain')
  const { t: tProduct } = useTranslation('features/product')
  const { enqueueSnackbar } = useSnackbar()
  const [errors, setErrors] = useState<MutationInputError[]>([])

  const tParams = {
    action: value ? 'enabled' : 'disabled',
    name,
    product: tProduct(`product.${product}`),
  }

  const { mutateAsync } = useMutation<{ input: Variables }, Data>({
    mutation: UPDATE_DOMAIN_PRODUCT_ENABLED,
    onCompleted: async (data: Data) => {
      const {
        updateDomainProductEnabled: { errors },
      } = data

      if (errors && errors.length > 0) {
        setErrors(errors)
        enqueueSnackbar(t('list.product.switch.error', tParams), {
          variant: 'error',
        })
      } else {
        setErrors([])
        enqueueSnackbar(t('list.product.switch.success', tParams), {
          variant: value ? 'success' : 'warning',
        })
      }
    },
  })

  const mapper = ({ id, product, value }: SwitchVariables): Variables => ({
    enabledValue: value,
    id,
    productType: product,
  })

  const update = (variables: SwitchVariables): void =>
    void mutateAsync({
      input: mapper(variables),
    })

  return { errors, update }
}
